/**
 * CapturePage — Lanza el escáner de documentos.
 * Ruta: /captura
 */
import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDocumentScanner, type ScannerStatus, type ScannerErrorCode } from '@dicio/sdk-ocr-web-module';

import { AppHeader, Spinner }   from '../../shared/components';
import { useOtorgante }         from '../../shared/hooks';
import { applyTheme, getParam } from '../../shared/theme';
import { ROUTES }               from '../../config/routes';
import { env }                  from '../../config/env';
import type { CaptureLocationState } from './types';

export const CapturePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    apikey:        apikeyFromState,
    dataOtorgante: dataOtorganteFromState,
    formValues,
  } = (location.state as CaptureLocationState) ?? {};

  const apikey = apikeyFromState || env.blinkidApikey;

  // Parámetros del otorgante — reutiliza los del state si ya vienen cargados
  const { params: fetchedParams, loading: loadingOtorgante } = useOtorgante(
    dataOtorganteFromState?.length ? '' : apikey,
  );
  const otorganteParams = dataOtorganteFromState?.length
    ? dataOtorganteFromState
    : fetchedParams;

  useEffect(() => {
    if (otorganteParams?.length) applyTheme(otorganteParams);
  }, [otorganteParams]);

  // ── SDK ────────────────────────────────────────────────────────────────────
  const { status, error, scanResult, rawResult, startScanning, reset } = useDocumentScanner({
    apiKey: env.credentialsApikey,
  });

  // Ref para bloquear el spinner de forma imperativa cuando el usuario cancela.
  // Usamos ref en lugar de state para evitar problemas de timing entre renders.
  const cancelledRef = useRef(false);

  const isLoading = (status as ScannerStatus) === 'loading';
  const isActive  = (status as ScannerStatus) === 'loading'
                 || (status as ScannerStatus) === 'running';

  // El usuario cerró el escáner sin completar — navegar directamente al inicio
  useEffect(() => {
    if ((status as ScannerStatus) === 'cancelled') {
      reset();
      document.body.classList.remove('modal-open');
      navigate(ROUTES.home, { state: { dataOtorgante: otorganteParams } });
    } else if (status === 'idle' || status === 'error') {
      cancelledRef.current = false;
    }
  }, [status, reset, navigate, otorganteParams]);

  // Navegar a resultados cuando el scan termina con éxito
  useEffect(() => {
    if (status === 'success' && scanResult) {
      navigate(ROUTES.results, {
        state: {
          apikey,
          dataOtorgante: otorganteParams,
          scanResult,
          rawResult,
          formValues,
        },
      });
    }
  }, [status, scanResult, rawResult, apikey, otorganteParams, formValues, navigate]);

  const logoSrc     = getParam(otorganteParams ?? [], 'LOGO_OTORGANTE');
  const accentColor = getParam(otorganteParams ?? [], 'COLOR_ACENTUACION');

  // Errores recuperables — el usuario puede volver a intentarlo
  const RETRIABLE: ScannerErrorCode[] = [
    'tiempo-agotado',
    'documento-no-compatible',
    'resultado-no-disponible',
    'desconocido',
  ];
  const isRetriable = error ? RETRIABLE.includes(error.codigo) : false;

  // Spinner solo durante la carga inicial del SDK — en `running` el SDK ya tiene su propia UI
  if (loadingOtorgante || isLoading) return <Spinner />;

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader logoSrc={logoSrc || undefined} />

      <main className="mx-auto flex max-w-sm flex-col items-center px-4 py-16">
        <div className="w-full rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
          <h1 className="mb-2 text-center text-xl font-bold text-gray-900">
            Captura de identificación
          </h1>
          <p className="mb-8 text-center text-sm text-gray-500">
            Asegúrate de tener buena iluminación y el documento a la mano.
          </p>

          {/* Bloque de error — contenido varía según el código */}
          {status === 'error' && error && (
            <div className={`mb-6 rounded-xl border p-4 ${
              isRetriable
                ? 'border-amber-100 bg-amber-50'
                : 'border-red-100 bg-red-50'
            }`}>
              {/* Ícono según severidad */}
              <p className={`text-sm font-semibold ${
                isRetriable ? 'text-amber-700' : 'text-red-700'
              }`}>
                {isRetriable ? '⚠️ Intento fallido' : '❌ Error de acceso'}
              </p>
              <p className={`mt-1 text-sm ${
                isRetriable ? 'text-amber-600' : 'text-red-600'
              }`}>
                {error.mensaje}
              </p>

              {/* Instrucción adicional para errores de cámara */}
              {error.codigo === 'camara-no-disponible' && (
                <p className="mt-2 text-xs text-red-500">
                  Ve a los ajustes de tu navegador y permite el acceso a la cámara para este sitio.
                </p>
              )}
              {error.codigo === 'inicializacion-fallida' && (
                <p className="mt-2 text-xs text-red-500">
                  Verifica tu conexión a internet y recarga la página.
                </p>
              )}
            </div>
          )}

          {/* Botón principal — solo se muestra si el error es recuperable o no hay error */}
          {(status !== 'error' || isRetriable) && (
            <button
              onClick={startScanning}
              disabled={isLoading}
              className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-50"
              style={accentColor ? { backgroundColor: accentColor } : undefined}
            >
              {status === 'error' ? 'Reintentar escaneo' : 'Iniciar escaneo'}
            </button>
          )}

          <button
            onClick={() => {
              reset();
              navigate(ROUTES.home, { state: { dataOtorgante: otorganteParams } });
              document.body.classList.remove('modal-open');
            }}
            className="btn-secondary mt-3 w-full"
          >
            Cancelar
          </button>
        </div>
      </main>
    </div>
  );
};
